/**
 * The slicer strips under the chart: one row per slicer, dragged live.
 *
 * Each row is a histogram of that column over the points already loaded, with
 * a two-handle range on top. Dragging redraws the chart from the cached
 * response (`sliceResponse` blanks the points outside) - no request. The link
 * and the row estimate are brought up to date when the drag ends, not on every
 * frame.
 *
 * The values come from whatever is drawn: the single response, every ship of a
 * multi-ship plot, or every period of a comparison - so one slicer ranges over
 * all of them together. A slicer on a column the last Plot did not fetch says
 * so and waits for the next one.
 *
 * **Play** slides the slicer's window across the range: the band you set is the
 * window (untouched, it starts at a tenth of the range), and it crosses the
 * whole range in the chosen sweep time, looping or stopping at the end. Every
 * frame is an in-place redraw on a single-ship scatter (`slice-redraw.ts`), so
 * it stays smooth on a full 100k-point cloud. The link and the row estimate
 * catch up when it stops, not on every frame.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { filters, type FilterRow } from '../../state/store.ts';
import { plotState } from '../../state/plot-state.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import type { AnyPlotResponse, PlotResponse } from '../../api/types.ts';
import { runtime } from './plot-runtime.ts';
import { lastPlotData } from './plot-ui-state.ts';
import { rerenderChartFromLast, settleGaugeSlices } from './plot-actions.ts';
import { gaugeColumnArrays } from './gauge-slice.ts';
import { redrawSlices } from './slice-redraw.ts';
import { formatNum, fmtTimeShort } from './render/format.ts';
import {
  columnValues,
  isNarrowed,
  isSliceable,
  isTimeColumn,
  pinSlicer,
  setSlicerRange,
  slicerRows,
  type SliceableResponse,
  type SlicerRange,
} from './slicers.ts';

const PLAY_GLYPH = svg`<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 1.2v7.6L8.6 5z" fill="currentColor"/></svg>`;
const PAUSE_GLYPH = svg`<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect x="2" y="1.5" width="2.2" height="7" rx="0.6" fill="currentColor"/><rect x="5.8" y="1.5" width="2.2" height="7" rx="0.6" fill="currentColor"/></svg>`;

const BUCKETS = 48;
const MIN_SHARE = 0.002;

interface Profile {
  min: number;
  max: number;
  bars: number[];
}

/** Every sliceable response currently drawn (line, scatter, map) - one, one
 *  per ship, or one per period. */
function drawnResponses(): SliceableResponse[] {
  if (runtime.lastByPeriod) return runtime.lastByPeriod.map((r) => r.data).filter(isSliceable);
  if (runtime.lastData?.plot_type === 'gauge') return [];
  if (runtime.lastByShip && runtime.lastByShip.length > 1) {
    return runtime.lastByShip.map((r) => r.data).filter(isSliceable);
  }
  return isSliceable(runtime.lastData) ? [runtime.lastData] : [];
}

/** The column's extent and histogram across everything drawn, or null if not loaded. */
function profile(column: string): Profile | null {
  const arrays = [
    ...drawnResponses()
      .map((d) => columnValues(d, column))
      .filter((a): a is ArrayLike<number | null> => !!a),
    // A gauge's readings, one array per ship.
    ...gaugeColumnArrays(runtime.lastData?.plot_type === 'gauge' ? runtime.lastData : null, column),
  ];
  if (!arrays.length) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const a of arrays) {
    for (let i = 0; i < a.length; i++) {
      const v = a[i];
      if (v == null || !Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!(max > min)) return null;
  const counts = new Array<number>(BUCKETS).fill(0);
  for (const a of arrays) {
    for (let i = 0; i < a.length; i++) {
      const v = a[i];
      if (v == null || !Number.isFinite(v)) continue;
      counts[Math.min(BUCKETS - 1, Math.floor(((v - min) / (max - min)) * BUCKETS))]!++;
    }
  }
  const top = Math.max(1, ...counts);
  return { min, max, bars: counts.map((c) => c / top) };
}

/** Seconds Play takes to sweep the whole range, in the order the chip cycles. */
const SWEEPS = [10, 30, 60, 5];
/** The window Play slides when the slicer has not been narrowed: a tenth of the range. */
const DEFAULT_WINDOW = 0.1;

@customElement('slicer-strip')
export class SlicerStrip extends LightElement {
  private store = new StoreController(this, [filters, lastPlotData, plotState]);

  /** The row being played, or null. One at a time: two moving windows would fight. */
  @state() private playing: string | null = null;
  /** Seconds to sweep the whole range. */
  @state() private sweep = SWEEPS[0]!;
  @state() private loop = true;
  private frame = 0;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stop();
  }

  private togglePlay(row: FilterRow & { slicer: SlicerRange }, p: Profile): void {
    if (this.playing === row.id) {
      this.stop();
      return;
    }
    this.stop();
    const w = p.max - p.min;
    const [a0, b0] = this.shares(row.slicer, p);
    // The band is the window. Untouched - or already the whole range - it
    // starts as a tenth of the range; at the end already, it starts over.
    let width = b0 - a0;
    let start = a0;
    if (!isNarrowed(row.slicer) || width >= 0.999) {
      width = DEFAULT_WINDOW;
      start = 0;
    }
    if (start + width >= 0.9995) start = 0;
    this.playing = row.id;
    let last = performance.now();
    let pos = start;
    const step = (now: number): void => {
      if (this.playing !== row.id) return;
      const dt = Math.min(100, now - last);
      last = now;
      pos += ((1 - width) * dt) / (this.sweep * 1000);
      if (pos + width >= 1) {
        if (this.loop) pos = 0;
        else {
          pos = 1 - width;
          this.apply(row.id, p, pos, width, w);
          this.stop();
          return;
        }
      }
      this.apply(row.id, p, pos, width, w);
      this.frame = requestAnimationFrame(step);
    };
    this.apply(row.id, p, pos, width, w);
    this.frame = requestAnimationFrame(step);
  }

  private apply(id: string, p: Profile, pos: number, width: number, w: number): void {
    setSlicerRange(id, { min: p.min + pos * w, max: p.min + (pos + width) * w });
    redrawSlices();
  }

  /** Stop playing and bring the link and the row estimate up to date. */
  private stop(): void {
    cancelAnimationFrame(this.frame);
    if (this.playing === null) return;
    this.playing = null;
    syncToUrl();
    scheduleEstimate();
    void settleGaugeSlices();
  }

  private cycleSweep(): void {
    const i = SWEEPS.indexOf(this.sweep);
    this.sweep = SWEEPS[(i + 1) % SWEEPS.length]!;
  }

  private fmt(column: string, v: number): string {
    return isTimeColumn(column) ? fmtTimeShort(v) : formatNum(v);
  }

  private shares(range: SlicerRange, p: Profile): [number, number] {
    const w = p.max - p.min;
    const a = range.min == null ? 0 : Math.min(1, Math.max(0, (range.min - p.min) / w));
    const b = range.max == null ? 1 : Math.min(1, Math.max(0, (range.max - p.min) / w));
    return [a, Math.max(b, a + MIN_SHARE)];
  }

  private onDown(e: PointerEvent, row: FilterRow & { slicer: SlicerRange }, p: Profile): void {
    if (e.button !== 0) return;
    this.stop();
    const track = e.currentTarget as HTMLElement;
    const target = e.target as HTMLElement;
    const mode = target.classList.contains('is-lo')
      ? 'lo'
      : target.classList.contains('is-hi')
        ? 'hi'
        : target.classList.contains('slicer-band')
          ? 'pan'
          : 'jump';
    e.preventDefault();
    const rect = track.getBoundingClientRect();
    const along = (ev: PointerEvent): number => Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
    const [a0, b0] = this.shares(row.slicer, p);
    const start = along(e);
    try {
      track.setPointerCapture(e.pointerId);
    } catch {
      // A synthetic or already-ended pointer - the listeners below still work.
    }
    track.classList.add('is-dragging');
    let frame = 0;
    const move = (ev: PointerEvent): void => {
      const q = along(ev);
      let a = a0;
      let b = b0;
      if (mode === 'lo') a = Math.min(q, b0 - MIN_SHARE);
      else if (mode === 'hi') b = Math.max(q, a0 + MIN_SHARE);
      else if (mode === 'pan') {
        const d = Math.min(1 - b0, Math.max(-a0, q - start));
        a = a0 + d;
        b = b0 + d;
      } else {
        // Pressed on the bare track: move the nearer end there.
        if (Math.abs(q - a0) < Math.abs(q - b0)) a = Math.min(q, b0 - MIN_SHARE);
        else b = Math.max(q, a0 + MIN_SHARE);
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const w = p.max - p.min;
        // Ends at the data's own bounds are stored open, so an untouched side
        // stays "everything" even when the next Plot brings in wider data.
        setSlicerRange(row.id, {
          min: a <= 0.0005 ? null : p.min + a * w,
          max: b >= 0.9995 ? null : p.min + b * w,
        });
        redrawSlices();
      });
    };
    const up = (ev: PointerEvent): void => {
      if (track.hasPointerCapture(ev.pointerId)) track.releasePointerCapture(ev.pointerId);
      track.classList.remove('is-dragging');
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
      track.removeEventListener('pointercancel', up);
      // The link and the row estimate follow the slicer once it is let go,
      // and a gauge drawn from thinned readings asks for its exact figures.
      requestAnimationFrame(() => {
        syncToUrl();
        scheduleEstimate();
        void settleGaugeSlices();
      });
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);
    if (mode === 'jump') move(e);
  }

  private reset(row: FilterRow): void {
    setSlicerRange(row.id, { min: null, max: null });
    redrawSlices();
    syncToUrl();
    scheduleEstimate();
  }

  private pin(row: FilterRow): void {
    pinSlicer(row.id);
    rerenderChartFromLast();
    syncToUrl();
    scheduleEstimate();
  }

  private renderRow(row: FilterRow & { slicer: SlicerRange }): TemplateResult {
    const p = profile(row.column);
    if (!p) {
      return html`<div class="slicer-row is-waiting">
        <span class="slicer-name" title=${row.column}>${row.column}</span>
        <span class="slicer-wait">Press Plot to load this column for slicing</span>
      </div>`;
    }
    const [a, b] = this.shares(row.slicer, p);
    const narrowed = isNarrowed(row.slicer);
    const lo = row.slicer.min ?? p.min;
    const hi = row.slicer.max ?? p.max;
    return html`<div class="slicer-row ${narrowed ? 'is-narrowed' : ''}">
      <span class="slicer-name" title=${row.column}>${row.column}</span>
      <div
        class="slicer-track"
        title="Drag the ends to narrow, the middle to move - double-click to show everything"
        @pointerdown=${(e: PointerEvent) => this.onDown(e, row, p)}
        @dblclick=${() => this.reset(row)}
      >
        <div class="slicer-hist" aria-hidden="true">
          ${p.bars.map((v, i) => {
            const inside = (i + 0.5) / p.bars.length >= a && (i + 0.5) / p.bars.length <= b;
            return html`<span class=${inside ? 'is-in' : ''} style=${`--fill:${Math.max(0.05, v)}`}></span>`;
          })}
        </div>
        <div class="slicer-band" style=${`left:${a * 100}%;width:${(b - a) * 100}%`}>
          <span class="slicer-handle is-lo"></span>
          <span class="slicer-handle is-hi"></span>
        </div>
      </div>
      <span class="slicer-readout">${this.fmt(row.column, lo)} – ${this.fmt(row.column, hi)}</span>
      <span class="slicer-play-group">
        <button
          class="slicer-play ${this.playing === row.id ? 'is-playing' : ''}"
          title=${this.playing === row.id ? 'Pause' : 'Play: slide this window across the range'}
          aria-label=${this.playing === row.id ? 'Pause' : 'Play'}
          @click=${() => this.togglePlay(row, p)}
        >
          ${this.playing === row.id ? PAUSE_GLYPH : PLAY_GLYPH}
        </button>
        <button class="slicer-chip" title="Time to sweep the whole range - click to change" @click=${() => this.cycleSweep()}>
          ${this.sweep}s
        </button>
        <button
          class="slicer-chip ${this.loop ? 'is-on' : ''}"
          title=${this.loop ? 'Looping - click to stop at the end' : 'Stops at the end - click to loop'}
          aria-pressed=${this.loop ? 'true' : 'false'}
          @click=${() => (this.loop = !this.loop)}
        >
          ↻
        </button>
      </span>
      ${narrowed
        ? html`<button
            class="slicer-act"
            title="Pin: make this range a real filter - the next Plot queries exactly this slice at full detail"
            @click=${() => this.pin(row)}
          >
            Pin
          </button>`
        : nothing}
    </div>`;
  }

  override render(): TemplateResult | typeof nothing {
    const t = plotState.value.plot_type;
    const rows = slicerRows();
    if (!rows.length || (t !== 'line' && t !== 'scatter' && t !== 'gauge' && t !== 'map')) return nothing;
    return html`<div class="slicer-strip">${rows.map((r) => this.renderRow(r))}</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'slicer-strip': SlicerStrip;
  }
}
